import styled from 'styled-components';
import {
  space,
  width,
  style,
  color,
  height,
  border,
  system,
  display,
  position,
  fontSize,
  minWidth,
  minHeight,
  maxHeight,
  overflowY,
  borderRadius,
} from 'styled-system';
// helpers/constants
import * as G from '../helpers';
import * as GC from '../constants';
// ui
import { Box, FixedBox } from './box';
//////////////////////////////////////////////////

export const Wrapper = styled.div`
  z-index: 12;
  position: relative;

  display: ${({ display }) => display || 'flex'};
`;

export const FooterWrapper = styled.div`
  ${space}

  width: 100%;
  display: flex;
  justify-content: center;
`;

export const RootWrapper = styled.div`
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow-y: auto;
  position: absolute;
  overflow-x: hidden;
`;

export const LayoutWrapper = styled.div`
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1300;
  position: fixed;
`;

export const PageWrapper = styled.div`
  ${width}
  ${space}

  position: relative;

  z-index: ${({ zi }) => zi};
  min-width: ${({ minWidth }) => minWidth};
  max-height: ${({ maxHeight }) => maxHeight};
  min-height: ${({ minHeight }) => minHeight};
  height: ${({ height }) => height || '100vh'};
  overflow: ${({ overflow }) => overflow || 'auto'};
  padding-left: ${({ pl }) => `${pl || GC.PAGE_WRAPPER_PADDING_LEFT}px`};
  padding-bottom: ${({ pb }) => `${pb || GC.PAGE_WRAPPER_PADDING_BOTTOM}px`};
  background-color: ${({ bgColor }) => bgColor || G.getTheme('colors.light.mainLight')};

  &::-webkit-scrollbar-thumb
  {
    background-color: ${() => G.getTheme('scroll.bgColor')};
  }
`;

export const ListWrapper = styled(PageWrapper)`
  padding-bottom: 0;
`;

ListWrapper.defaultProps = {
  pl: 60,
  bgColor: G.getTheme('colors.#F8FAFD'),
};

export const PageHeaderWrapper = styled.div`
  ${space}
  ${width}
  ${height}
  ${overflowY}

  display: flex;

  top: ${({ top }) => top};
  left: ${({ left }) => left};
  z-index: ${({ zI }) => zI || 'auto'};
  box-shadow: ${({ boxShadow }) => boxShadow};
  justify-content: ${({ justify }) => justify};
  align-items: ${({ aI }) => aI || 'flex-end'};
  background-color: ${({ bgColor }) => bgColor};
  position: ${({ position }) => position || 'static'};

  &::-webkit-scrollbar { height: 3px };
`;

PageHeaderWrapper.defaultProps = {
  pt: 15,
  height: GC.PAGE_HEADER_WRAPPER_HEIGHT,
};

export const ListsPagesHeaderWrapper = styled.div`
  height: 60px;
  display: flex;
  padding: 10px;
  align-items: center;
  margin-bottom: 15px;

  color: ${() => G.getTheme('pages.listsPages.header.textColor')};
  background-color: ${() => G.getTheme('pages.listsPages.header.bgColor')};
  border-color: ${() => G.getTheme('pages.listsPages.header.borderColor')};
`;

export const SimpleWrapper = styled.div`
  ${space}
  ${width}
  ${height}
  ${border}
  ${minWidth}
  ${minHeight}
  ${maxHeight}

  z-index: ${({ zi }) => zi};
  color: ${({ color }) => color};
  justify-content: ${({ jc }) => jc};
  overflow:  ${({ overflow }) => overflow};
  display: ${({ display }) => display || 'flex'};
  flex-direction: ${({ flexDirection }) => flexDirection};
  align-items: ${({ alignItems }) => alignItems || 'center'};
`;

export const RelativeWrapper = styled.div`
  ${width}
  ${space}

  position: relative;

  display: ${({ display }) => display || 'flex'};
`;

export const AbsoluteWrapper = styled.div`
  ${width}
  ${space}

  position: absolute;

  top: ${({ top }) => top || 'initial'};
  left: ${({ left }) => left || 'initial'};
  right: ${({ right }) => right || 'initial'};
  z-index: ${({ zIndex }) => zIndex || 'auto'};
  bottom: ${({ bottom }) => bottom || 'initial'};
  display: ${({ display }) => display || 'flex'};
`;

export const PageTitleCount = styled.div`
  ${width}
  ${height}
  ${fontSize}

  color: white;
  display: flex;
  position: absolute;
  align-items: center;
  justify-content: center;

  padding: ${({ p }) => p || 0};
  top: ${({ t }) => t || '-15px'};
  left: ${({ l }) => l || 'unset'};
  right: ${({ r }) => r || '-15px'};
  border-radius: ${({ br }) => br || '50%'};
  border: 2px solid ${() => G.getTheme('pages.listTitle.countBorderColor')};
  background-color: ${({ bgColor }) => bgColor || G.getTheme('colors.dark.blue')};
`;

PageTitleCount.defaultProps = {
  width: 30,
  height: 30,
  fontSize: 16,
};

export const IconWrapper = styled.div`
  ${space}
  ${width}
  ${height}

  cursor: ${({ cursor }) => cursor};

  ${({ disabled }) => disabled && `
    opacity: 0.5;
    cursor: not-allowed;
  `}
`;

export const IconWrapperPositioned = styled(IconWrapper)`
  position: ${({ position }) => position};
`;

export const ZOrderWrapper = styled.div`
  ${space}

  z-index: ${({ zIndex }) => zIndex || 'auto'};
  position: ${({ position }) => position || 'relative'};
`;

export const StickedWrapper = styled.div`
  ${space}
  ${width}
  ${height}
  ${position}
  ${minWidth}

  background-color: ${({ bgColor }) => bgColor};
`;

StickedWrapper.defaultProps = {
  height: 'unset',
  position: 'sticky',
};

// demo last
export const textTransform = style({
  key: 'textTransform',
  prop: 'textTransform',
  cssProp: 'textTransform',
  transformValue: (n) => n,
});

export const Text = styled.div`
  ${width}
  ${space}
  ${color}
  ${fontSize}
  ${textTransform}

  cursor: ${({ cursor }) => cursor};
  font-weight: ${({ fontWeight }) => fontWeight};
  word-break: ${({ wordBreak }) => wordBreak || 'break-all'};

  &:hover {
    color: ${({ hoverColor }) => hoverColor};
    cursor: ${({ hoverCursor }) => hoverCursor};
  }
`;

export const EditorWrapper = styled(Box)`
  border-radius: 4px;
  border: 1px solid ${({ hasError }) => G.ifElse(
    hasError,
    G.getTheme('forms.inputs.borderColorErr'),
    G.getTheme('colors.dark.grey'),
  )};

  & .DraftEditor-root {
    z-index: 9;
    padding: 0 15px;
  }

  & .rdw-editor-toolbar {
    background-color: ${() => G.getTheme('colors.light.lightGrey')};
  }

  & .rdw-emoji-modal, .rdw-link-modal {
    right: 0;
    left: auto;
  }

  & .rdw-emoji-modal {
    width: 215px;
  }

  & .rdw-link-modal {
    height: 230px;
  }

  & .rdw-link-decorator-wrapper a > span > span, .public-DraftStyleDefault-block > span > span {
    font-family: inherit;
  }

  & pre {
    white-space: normal;
  }
`;

export const EditorText = styled(Box)`
  word-break: break-word;

  & pre {
    white-space: normal;
  }
`;

export const PopupContentWrapper = styled(FixedBox)`
  ${borderRadius}

  width: max-content;
  z-index: ${({ zIndex }) => zIndex || 12};
  top: ${({ positions }) => positions.top}px;
  background-color: ${() => G.getTheme('colors.white')};
  margin: ${({ positions }) => positions.margin};
  box-shadow: ${() => `0 1px 8px 0 ${G.getTheme('colors.boxShadowGrey')}`};

  &[type='center'] {
    right: ${({ positions }) => positions.right}px;
  }

  &[type='right'] {
    right: ${({ positions }) => positions.right}px;
  }

  &[type='left'] {
    left: ${({ positions }) => positions.right}px;
  }

  &[type='target-right'] {
    left: ${({ positions }) => positions.left}px;
  }
`;

PopupContentWrapper.defaultProps = {
  p: '5px 0',
  borderRadius: '2px',
};

const afterPosition = system({
  afterTop: {
    property: 'top',
    transform: (value) => G.ifElse(G.isNumber(value), `${value}px`, value),
  },
  afterRight: {
    property: 'right',
    transform: (value) => G.ifElse(G.isNumber(value), `${value}px`, value),
  },
});

export const SelectWrapper = styled.div`
  ${width}
  ${display}

  position: relative;

  &:after {
    width: 6px;
    height: 6px;
    content: '';
    border: solid;
    position: absolute;
    pointer-events: none;
    border-width: 0px 2px 2px 0;
    transition: border-color 0.4s;
    transform: rotate(45deg) translate(0, -60%);
    border-color: ${G.getTheme('colors.dark.grey')};

    ${afterPosition}
  }

  &:hover:after {
    border-color: ${G.getTheme('colors.light.darksGrey')};
  }
`;

SelectWrapper.defaultProps = {
  afterTop: 18,
  afterRight: 25,
};
